'use memo';

import { useRef } from 'react';
import Input from '@/components/Input/Input';
import { HStack, VStack } from '@/components/Layout';
import SelectBank from '@/components/Select/SelectBank';
import SelectBankBranch from '@/components/Select/SelectBankBranch';
import { SelectField } from '@/components/Select/SelectField';
import type { CodeNameEntry } from '@/components/Select/useBanks';
import { Text } from '@/components/Typography';
import type { BankAccountValue } from '@/features/section-attribute/types';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
type AccountType = BankAccountValue['accountType'];
type Props = CFormItemProps & {
  value: BankAccountValue;
  onChange: (value: BankAccountValue) => void;
};
function LabelValue({
  label,
  value
}: {
  label: string;
  value?: string;
}) {
  return <VStack className="gap-0.5">
      <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
        {label}
      </Text>
      <Text variant="p">{value || '-'}</Text>
    </VStack>;
}
export const CFormBankAccountItem = buildCFormItem((props: Props) => {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.bankAccountItem'
  });
  const val = props.value;
  const branchTriggerRef = useRef<HTMLButtonElement>(null);
  const ACCOUNT_TYPE_OPTIONS: {
    id: AccountType;
    label: string;
  }[] = [{
    id: 'ORDINARY',
    label: t('accountType.ORDINARY')
  }, {
    id: 'CHECKING',
    label: t('accountType.CHECKING')
  }, {
    id: 'SAVINGS',
    label: t('accountType.SAVINGS')
  }];
  const accountTypeLabel = (type: AccountType) => ACCOUNT_TYPE_OPTIONS.find(o => o.id === type)?.label ?? type;
  const handleField = (field: keyof BankAccountValue, v: string) => {
    props.onChange({
      ...val,
      [field]: v
    });
  };
  const handleBankChange = (entry?: CodeNameEntry) => {
    if (entry) {
      props.onChange({
        ...val,
        bankCode: entry.code,
        bankName: entry.name,
        branchCode: '',
        branchName: ''
      });
      requestAnimationFrame(() => {
        branchTriggerRef.current?.focus();
      });
    } else {
      props.onChange({
        ...val,
        bankCode: '',
        bankName: '',
        branchCode: '',
        branchName: ''
      });
    }
  };
  const handleBranchChange = (entry?: CodeNameEntry) => {
    if (entry) {
      props.onChange({
        ...val,
        branchCode: entry.code,
        branchName: entry.name
      });
    } else {
      props.onChange({
        ...val,
        branchCode: '',
        branchName: ''
      });
    }
  };
  const readComponent = <VStack className="gap-3">
      <HStack className="gap-8">
        <LabelValue label={t('bankName')} value={val.bankName && val.bankCode ? `${val.bankName} (${val.bankCode})` : val.bankName} />
        <LabelValue label={t('branchName')} value={val.branchName && val.branchCode ? `${val.branchName} (${val.branchCode})` : val.branchName} />
      </HStack>
      <HStack className="gap-8">
        <LabelValue label={t('accountType.label')} value={accountTypeLabel(val.accountType)} />
        <LabelValue label={t('accountNumber')} value={val.accountNumber} />
      </HStack>
      <LabelValue label={t('accountHolderName')} value={val.accountHolderName} />
    </VStack>;
  const bankValue = val.bankCode ? {
    code: val.bankCode,
    name: val.bankName
  } : undefined;
  const branchValue = val.branchCode ? {
    code: val.branchCode,
    name: val.branchName
  } : undefined;
  const writeComponent = <VStack className="gap-3">
      <VStack className="gap-1">
        <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
          {t('bankName')}
        </Text>
        <SelectBank value={bankValue} onChange={handleBankChange} allowEmpty={!props.required} disabled={props.readonly} rootClassName="w-full" />
      </VStack>
      <VStack className="gap-1">
        <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
          {t('branchName')}
        </Text>
        <SelectBankBranch bankCode={val.bankCode || undefined} value={branchValue} onChange={handleBranchChange} allowEmpty={!props.required} disabled={props.readonly} rootClassName="w-full" triggerRef={branchTriggerRef} />
      </VStack>
      <VStack className="gap-1">
        <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
          {t('accountType.label')}
        </Text>
        <SelectField value={val.accountType} options={ACCOUNT_TYPE_OPTIONS} placeholder={t('accountType.label')} onValueChange={v => handleField('accountType', v)} disabled={props.readonly} />
      </VStack>
      <VStack className="gap-1">
        <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
          {t('accountNumber')}
        </Text>
        <Input width="100%" value={val.accountNumber} onChange={e => handleField('accountNumber', e.target.value)} disabled={props.readonly} aria-label={t('accountNumber')} />
      </VStack>
      <VStack className="gap-1">
        <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
          {t('accountHolderName')}
        </Text>
        <Input width="100%" value={val.accountHolderName} onChange={e => handleField('accountHolderName', e.target.value)} disabled={props.readonly} aria-label={t('accountHolderName')} />
      </VStack>
    </VStack>;
  return <CFormItemBase {...props} readComponent={readComponent} writeComponent={writeComponent} />;
});